import { useMutation } from "@tanstack/react-query";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

import { getPosClient } from "../../lib/api";

interface MfaSetupGateProps {
  readonly onEnrolled: () => void;
}

/**
 * Full-screen blocking gate shown when mfa_required=true but mfa_enrolled=false.
 * The user cannot proceed until they complete TOTP enrollment.
 */
export function MfaSetupGate({ onEnrolled }: MfaSetupGateProps) {
  const { t } = useTranslation();
  const [otpUri, setOtpUri] = useState<string | null>(null);
  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const setup = useMutation({
    mutationFn: () => getPosClient().mfaSetup(),
    onSuccess: (data) => {
      setOtpUri(data.otp_uri);
      setSecretCode(data.secret_code);
    },
  });

  const verify = useMutation({
    mutationFn: (userCode: string) => getPosClient().mfaVerify({ user_code: userCode }),
    onSuccess: () => {
      setDone(true);
      setTimeout(onEnrolled, 1500);
    },
    onError: () => {
      setCodeError(t("profile.mfa.invalidCode"));
    },
  });

  useEffect(() => {
    setup.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setCodeError(null);
    if (!/^\d{6}$/.test(code)) {
      setCodeError(t("profile.mfa.codeFormat"));
      return;
    }
    verify.mutate(code);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm space-y-6 rounded-2xl border border-line bg-surface p-8 shadow-elev-2">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/10">
            <ShieldCheck className="h-6 w-6 text-brand-primary" aria-hidden />
          </div>
          <h1 className="text-lg font-bold text-strong">{t("mfaGate.title")}</h1>
          <p className="text-sm text-muted">{t("mfaGate.body")}</p>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <Check className="h-10 w-10 text-emerald-600" aria-hidden />
            <p className="font-semibold text-emerald-700 dark:text-emerald-400">
              {t("mfaGate.success")}
            </p>
          </div>
        ) : setup.isPending || (!otpUri && !setup.isError) ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            {t("profile.mfa.loadingSetup")}
          </div>
        ) : setup.isError ? (
          <div className="space-y-3">
            <p className="text-center text-sm text-red-600 dark:text-red-400">
              {t("profile.mfa.setupError")}
            </p>
            <button
              type="button"
              onClick={() => setup.mutate()}
              className="w-full rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-foreground hover:bg-surface-muted"
            >
              {t("common.retry")}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-center text-sm text-foreground">
              {t("profile.mfa.scanInstruction")}
            </p>
            <div className="flex justify-center rounded-lg border border-line bg-white p-4">
              <QRCodeSVG value={otpUri ?? ""} size={160} aria-label={t("login.mfaSetup.qrAlt")} />
            </div>
            {secretCode && (
              <p className="text-center text-xs text-muted">
                {t("profile.mfa.manualEntry")}{" "}
                <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-xs text-strong select-all">
                  {secretCode}
                </code>
              </p>
            )}
            <form onSubmit={handleVerify} className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wider text-muted">
                  {t("profile.mfa.codeLabel")}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                    setCodeError(null);
                  }}
                  placeholder="000000"
                  className="mt-1 block w-full rounded-md bg-surface px-3 py-2 text-center font-mono text-lg tracking-widest text-strong ring-1 ring-inset ring-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                />
                {codeError && (
                  <span className="mt-1 block text-xs text-red-600 dark:text-red-400">
                    {codeError}
                  </span>
                )}
              </label>
              <button
                type="submit"
                disabled={code.length !== 6 || verify.isPending}
                className="w-full rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {verify.isPending ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {t("profile.mfa.verifying")}
                  </span>
                ) : (
                  t("profile.mfa.confirm")
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
